/**
 * CareerAbilityService — resolves mechanical effects of career special ability traits.
 *
 * Checks character traits for career abilities and returns applicable modifiers
 * for the current combat/roll context.
 */

import type { Character, Weapon, Trait } from '../types/character';
import type { Modifier } from '../types/dice';
import { ModifierSource } from '../types/dice';

/** Weapon Master: +10 hit, +2 damage, +2 initiative with chosen weapon class */
function getWeaponMasterModifiers(
  traits: Trait[],
  weapon: Weapon
): { attackMod: Modifier | null; damageMod: Modifier | null; initMod: Modifier | null } {
  const wmTrait = traits.find((t) => t.effect?.startsWith('weaponMaster:'));
  if (!wmTrait) return { attackMod: null, damageMod: null, initMod: null };

  const chosenClass = wmTrait.effect!.split(':')[1]; // e.g. "Bolt", "Las"
  if (!chosenClass) return { attackMod: null, damageMod: null, initMod: null };

  // Match against weapon's proficiencyRequired (e.g. "Bolt", "Las", "Melta")
  const weaponType = (weapon as any).proficiencyRequired as string | undefined;
  if (!weaponType || weaponType.toLowerCase() !== chosenClass.toLowerCase()) {
    return { attackMod: null, damageMod: null, initMod: null };
  }

  return {
    attackMod: {
      name: 'Weapon Master',
      value: 10,
      source: ModifierSource.Trait,
      description: `Weapon Master (${chosenClass}): +10 to hit`
    },
    damageMod: {
      name: 'Weapon Master',
      value: 2,
      source: ModifierSource.Trait,
      description: `Weapon Master (${chosenClass}): +2 damage`
    },
    initMod: {
      name: 'Weapon Master',
      value: 2,
      source: ModifierSource.Trait,
      description: `Weapon Master (${chosenClass}): +2 initiative`
    }
  };
}

/** Seeker of Lore: +1 DoS on Commerce, Inquiry, Evaluate tests */
function getSeekerOfLoreBonus(traits: Trait[], skillName: string): Modifier | null {
  if (!traits.some((t) => t.id === 'seeker-of-lore')) return null;

  const bonusSkills = ['commerce', 'inquiry', 'evaluate'];
  if (!bonusSkills.includes(skillName.toLowerCase())) return null;

  return {
    name: 'Seeker of Lore',
    value: 1, // +1 DoS (applied as bonus degree, not modifier to roll)
    source: ModifierSource.Trait,
    description: `Seeker of Lore: +1 bonus Degree of Success on ${skillName}`
  };
}

/** Check if character has The Eye is Open (immune to Warp Shock CP) */
function hasEyeIsOpen(traits: Trait[]): boolean {
  return traits.some((t) => t.id === 'the-eye-is-open');
}

/** Check if character has Soul-Bound (+20 WP vs daemons) */
function getSoulBoundModifier(traits: Trait[], isDaemonicContext: boolean): Modifier | null {
  if (!traits.some((t) => t.id === 'soul-bound')) return null;
  if (!isDaemonicContext) return null;

  return {
    name: 'Soul-Bound',
    value: 20,
    source: ModifierSource.Trait,
    description: 'Soul-Bound to the Emperor: +20 WP vs daemons'
  };
}

export class CareerAbilityService {
  /** Get attack roll modifiers from career abilities */
  static getAttackModifiers(character: Character, weapon: Weapon): Modifier[] {
    if (!character.traits) return [];
    const mods: Modifier[] = [];
    const wm = getWeaponMasterModifiers(character.traits, weapon);
    if (wm.attackMod) mods.push(wm.attackMod);
    return mods;
  }

  /** Get damage modifiers from career abilities */
  static getDamageModifiers(character: Character, weapon: Weapon): Modifier[] {
    if (!character.traits) return [];
    const mods: Modifier[] = [];
    const wm = getWeaponMasterModifiers(character.traits, weapon);
    if (wm.damageMod) mods.push(wm.damageMod);
    return mods;
  }

  /** Get initiative modifiers from career abilities */
  static getInitiativeModifiers(character: Character, weapon?: Weapon): Modifier[] {
    if (!character.traits || !weapon) return [];
    const mods: Modifier[] = [];
    const wm = getWeaponMasterModifiers(character.traits, weapon);
    if (wm.initMod) mods.push(wm.initMod);
    return mods;
  }

  /** Get skill roll modifiers from career abilities (Seeker of Lore) */
  static getSkillModifiers(character: Character, skillName: string): Modifier[] {
    if (!character.traits) return [];
    const mods: Modifier[] = [];
    const sol = getSeekerOfLoreBonus(character.traits, skillName);
    if (sol) mods.push(sol);
    return mods;
  }

  /** Check if character is immune to Warp Shock CP */
  static isImmuneToWarpShock(character: Character): boolean {
    if (!character.traits) return false;
    return hasEyeIsOpen(character.traits);
  }

  /** Get WP modifier for daemonic context (Soul-Bound) */
  static getSoulBoundModifier(character: Character, isDaemonicContext: boolean): Modifier | null {
    if (!character.traits) return null;
    return getSoulBoundModifier(character.traits, isDaemonicContext);
  }

  /**
   * Get initiative modifiers from traits alone (no weapon context needed).
   * Weapon Master +2 initiative always applies — the character is assumed to
   * have their chosen weapon class available in combat.
   */
  static getInitiativeModifiersFromTraits(character: Character): Modifier[] {
    if (!character.traits) return [];
    const wmTrait = character.traits.find((t) => t.effect?.startsWith('weaponMaster:'));
    if (!wmTrait) return [];
    const chosenClass = wmTrait.effect!.split(':')[1];
    return [
      {
        name: 'Weapon Master',
        value: 2,
        source: ModifierSource.Trait,
        description: `Weapon Master (${chosenClass}): +2 initiative`
      }
    ];
  }

  /**
   * Check if a skill is eligible for Seeker of Lore auto-succeed
   * (Ciphers, any Lore, or Logic skills).
   */
  static isSeekerOfLoreEligible(character: Character, skillId: string): boolean {
    if (!character.traits?.some((t) => t.id === 'seeker-of-lore')) return false;
    const id = skillId.toLowerCase();
    return id.includes('lore') || id === 'logic' || id.startsWith('ciphers');
  }
}
